import { Accordion } from './components/Accordion';
import { EstimationPeriodDropdown } from './components/EstimationPeriodDropdown';
import { NarrativeValuatorCalculation } from './components/NarrativeValuatorCalculation';
import { NarrativeValuatorInput } from './components/NarrativeValuatorInput';
import { TimeseriesPlot } from '../TimeseriesPlot';
import { CalculationBreakdown } from './components/CalculationBreakdown';
import { NarrativeFairValue } from './components/NarrativeFairValue';

import type { NarrativeForm } from '../../utils/schema';
import type { PastAnnualRevenueEarnings } from '../../types';
import type { NarrativeValuatorCompany } from '../../hooks/narrativeValuatorCompanyFragment';
import type { CalculationBreakDownOutput } from '../Calculators/';
import { getMostRecentReportedYear } from '../../utils/pastAnnualRevenueEarnings';
import { ShareholderToggleButton } from '../../ShareholderToggleButton';

export type Props = {
  company: NarrativeValuatorCompany;
  onChange: (data: Partial<NarrativeForm>) => void;
  onSetFairValue: (fairValue: number) => void;
  readOnly: boolean;
  values: NarrativeForm;
  initialFairValue: number;
  onReset: () => void;
  pastAnnualRevenueEarnings: PastAnnualRevenueEarnings;
  calculationBreakDownOutput: CalculationBreakDownOutput;
  owner: {
    displayName: string;
    publicId: string;
  };
  narrativeType: 'AI' | 'FAIR_VALUE' | 'LEGACY' | 'USER' | 'WARREN' | null;
};

export const NarrativeValuator = ({
  company,
  onChange,
  onSetFairValue,
  readOnly,
  values,
  initialFairValue,
  onReset,
  pastAnnualRevenueEarnings,
  calculationBreakDownOutput,
  owner,
  narrativeType,
}: Props) => {
  return (
    <>
      {readOnly ? (
        <NarrativeFairValue
          owner={owner}
          narrativeType={narrativeType}
          company={company}
          fairValue={values.fairValue}
          initialFairValue={initialFairValue}
          onReset={onReset}
        />
      ) : (
        <NarrativeValuatorCalculation
          company={company}
          fairValue={values.fairValue}
          onChange={onSetFairValue}
        />
      )}
      <EstimationPeriodDropdown
        timePeriod={values.timePeriod}
        onChange={(timePeriod) => onChange({ ...values, timePeriod })}
      />
      <div className="grid grid-cols-1 bg-p-grey-50">
        <TimeseriesPlot
          company={company}
          values={values}
          pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
        />
      </div>
      <NarrativeValuatorInput
        company={company}
        onChange={onChange}
        values={values}
      />
      <div className="mt-x1">
        <Accordion title="Other Inputs & Calculation">
          <CalculationBreakdown
            company={company}
            onChange={onChange}
            values={values}
            calculationBreakDownOutput={calculationBreakDownOutput}
            mostRecentReportedYear={getMostRecentReportedYear(
              pastAnnualRevenueEarnings
            )}
          />
        </Accordion>
      </div>
      {!readOnly && (
        <div className="pt-x1">
          <ShareholderToggleButton
            values={values}
            onChange={onChange}
            userHoldsPosition={values.userHoldsPosition ?? false}
          />
        </div>
      )}
    </>
  );
};
